import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Form from "@radix-ui/react-form";
import { AlertDialog, Callout, Dialog, Flex, Text } from "@radix-ui/themes";
import { useElevatedAccessToken, useMe, useSendVerification, useVerify, } from "../api";
import { useEffect, useRef, useState } from "react";
import { PrimaryButton, SecondaryButton } from "./elements";
import * as Otp from "./otp-input";
export function ElevatedAccess({ type = "dialog", children, onVerified, }) {
    const { elevatedAccess } = useElevatedAccessToken();
    const [authenticationChallengeId, setAuthenticationChallengeId] = useState();
    const prevAccessToken = useRef(elevatedAccess);
    useEffect(() => {
        prevAccessToken.current = elevatedAccess;
    }, [elevatedAccess]);
    if (elevatedAccess) {
        return _jsx(_Fragment, { children: children });
    }
    if (!authenticationChallengeId) {
        const hasTokenExpired = !!prevAccessToken.current;
        return (_jsx(SendVerificationEmailForm, { type: type, hasTokenExpired: hasTokenExpired, onSuccess: (challengeId) => {
                setAuthenticationChallengeId(challengeId);
            } }));
    }
    if (authenticationChallengeId) {
        return (_jsx(VerificationIdentityForm, { type: type, authenticationChallengeId: authenticationChallengeId, onSuccess: () => {
                // Reset the challenge id
                setAuthenticationChallengeId(undefined);
                return onVerified?.();
            } }));
    }
    return null;
}
function SendVerificationEmailForm({ onSuccess, type, hasTokenExpired, }) {
    const { data: me } = useMe();
    const sendVerification = useSendVerification({
        mutation: { onSuccess: (data) => onSuccess(data.authenticationChallenge) },
    });
    const Title = type === "dialog" ? Dialog.Title : AlertDialog.Title;
    const Description = type === "dialog" ? Dialog.Description : AlertDialog.Description;
    const Close = type === "dialog" ? Dialog.Close : AlertDialog.Cancel;
    const handleSubmit = async (event) => {
        event.preventDefault();
        sendVerification.mutate();
    };
    return (_jsxs("form", { onSubmit: handleSubmit, children: [_jsx(Title, { children: hasTokenExpired
                    ? "Your verification token has expired"
                    : "Verify your identity" }), _jsxs(Description, { color: "gray", mb: "5", children: ["To continue, we need to confirm your identity. We'll send a temporary verification code to", " ", _jsx(Text, { weight: "bold", highContrast: true, children: me?.email }), "."] }), sendVerification.error && (_jsx(Callout.Root, { color: "red", mt: "-2", mb: "0", children: _jsx(Callout.Text, { children: getMutationErrorMessage(sendVerification.error) }) })), _jsxs(Flex, { justify: "end", align: "center", gap: "3", mt: "5", children: [_jsx(Close, { children: _jsx(SecondaryButton, { type: "button", disabled: sendVerification.isPending, children: "Cancel" }) }), _jsx(PrimaryButton, { type: "submit", loading: sendVerification.isPending, children: "Send verification code" })] })] }));
}
function VerificationIdentityForm({ onSuccess, authenticationChallengeId, type, }) {
    const { data: me } = useMe();
    const { setElevatedAccess } = useElevatedAccessToken();
    const verifyIdentity = useVerify();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const Title = type === "dialog" ? Dialog.Title : AlertDialog.Title;
    const Description = type === "dialog" ? Dialog.Description : AlertDialog.Description;
    const Close = type === "dialog" ? Dialog.Close : AlertDialog.Cancel;
    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const code = formData.get("otp-code")?.toString() ?? "";
        setIsSubmitting(true);
        try {
            // Mutate async so we can wait for the onSuccess callback as well
            const newAuthState = await verifyIdentity.mutateAsync({
                data: {
                    code,
                    authenticationChallengeId,
                },
            });
            const in10Seconds = new Date(Date.now() + 5 * 1000);
            setElevatedAccess({
                token: newAuthState.elevatedAccessToken,
                // expiresAt: newAuthState.expiresAt,
                expiresAt: in10Seconds.toISOString(),
            });
            if (onSuccess) {
                await new Promise((resolve) => setTimeout(resolve, 200));
                await onSuccess();
            }
        }
        catch (error) {
            console.error(error);
        }
        setIsSubmitting(false);
    };
    return (_jsxs(Form.Root, { onSubmit: handleSubmit, children: [_jsx(Title, { children: "Verify your identity" }), _jsxs(Description, { color: "gray", children: ["A verification code was sent to", " ", _jsx(Text, { weight: "bold", highContrast: true, children: me?.email }), ". Please enter it below."] }), _jsxs(Flex, { direction: "column", gap: "2", mt: "5", mx: "auto", width: "fit-content", children: [_jsxs(Otp.Root, { autoSubmit: true, gap: "2", justify: "center", columns: "repeat(6, 48px)", width: "fit-content", rows: "48px", name: "otp-code", readOnly: isSubmitting, children: [_jsx(Otp.Input, { required: true, autoFocus: true, autoComplete: "off" }), _jsx(Otp.Input, { required: true }), _jsx(Otp.Input, { required: true }), _jsx(Otp.Input, { required: true }), _jsx(Otp.Input, { required: true }), _jsx(Otp.Input, { required: true })] }), verifyIdentity.error && (_jsx(Text, { color: "red", size: "2", as: "p", children: getMutationErrorMessage(verifyIdentity.error) }))] }), _jsxs(Flex, { justify: "end", align: "center", gap: "3", mt: "5", children: [_jsx(Close, { children: _jsx(SecondaryButton, { type: "button", children: "Cancel" }) }), _jsx(PrimaryButton, { type: "submit", loading: isSubmitting, children: "Confirm" })] })] }));
}
function getMutationErrorMessage(error) {
    let message = typeof error === "string" ? error : "";
    if (error instanceof Error) {
        message = error.message;
    }
    else if (typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof error.message === "string") {
        message = error.message;
    }
    if (!message || message === "Bad Request") {
        message = "Invalid code, please try again.";
    }
    return message;
}
//# sourceMappingURL=elevated-access.js.map